<template>
  <Button severity="secondary" @click="visible = true" :disabled="disabled">
    Select Metrics
  </Button>

  <DialogFrame
    v-if="visible"
    class="modal--widgets"
    :class="{'modal--widgets-preview': previewWidget}"
    :loading="loading"
    name="metric_add"
    size="ultra"
    @onClose="closeModal"
  >
    <template #title>Select Metrics</template>

    <template #body>
      <template v-if="previewWidget">
        <div class="widget-go-back" @click="resetPreview()">
          <IconGoBack />
          Go Back
        </div>

        <div class="widget-preview">
          <div class="widget-header">
            <div class="widget-title">
              {{ previewWidget.name }}
              <IconPremium v-if="previewWidget.isPremium" />
            </div>
          </div>
          <div class="widget-description">{{ previewWidget.previewText }}</div>
          <div class="widget-image-container">
            <img class="widget-image-preview" v-if="previewWidget.previewImage" :src="`/widget-previews/${previewWidget.previewImage}`" />
          </div>
        </div>
      </template>
      <template v-else>
        <p>Select metrics to appear on the dashboard.</p>
        <br />

        <Tabs :defaultTab="chosenCategory" v-if="widgetsByCategory">
          <Tab
            v-for="category in widgetsByCategory"
            :key="category.id"
            :id="category.id"
            :title="category.category"
            :icon="category.isPremium ? IconPremium : null"
          >
            <div v-if="!category.enabled" class="widget-explainer">
              <div>
                <div class="widget-explainer__title">Upgrade to Premium</div>
                <p>Upgrade to premium to use these widgets</p>
              </div>

              <Button link="/booking/new" severity="primary">
                <template #iconLeft><IconPremium /></template>
                Upgrade
              </Button>
            </div>
            <ul class="widget-list">
              <MetricCard
                v-for="widget in category.widgets"
                :key="widget.id"
                :widget="widget"
                :disabled="!category.enabled"
                :inUse="widgetInUse(widget.id)"
                @click="handleWidgetClicked(category.enabled, widget.id)"
                @onLearnMore="onLearnMore(widget)"
              />
            </ul>
          </Tab>
        </Tabs>
      </template>
    </template>

    <template #footer>
      <template v-if="previewWidget">
        <div class="center-buttons">
          <Button :loading="loading" severity="secondary" @click="resetPreview()">
            Go Back
          </Button>

          <Button :loading="loading" severity="primary" link="/booking/new">
            <template #iconLeft><IconPremium /></template>
            Upgrade to Premium
          </Button>
        </div>
      </template>
      <template v-else>
        <Button :loading="loading" severity="secondary" @click="closeModal()">
          Discard changes
        </Button>

        <Button :loading="loading" severity="primary" @click="saveChanges()" autofocus>
          Save changes
        </Button>
      </template>
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

import Button from '@/components/global/Button.vue';
import MetricCard from '@/components/widgets/Card.vue';
import DialogFrame from './DialogFrame.vue';
import Tabs from '@/components/global/Tabs.vue';
import Tab from '@/components/global/Tab.vue';

import IconGoBack from '@/icons/events/IconGoBack.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';

import { getWidgetById } from '@/utils/api';
import { useAnalyticsStore } from '@/stores/analytics';
import { useUserStore } from '@/stores/user';
import { useWidgets, type WidgetPreview } from '@/composables/useWidgets';

import type { WidgetObjectSorted } from '@/types/Widget';

defineProps<{
  disabled: boolean;
}>();

const emit = defineEmits(['onAddedWidget', 'onRemovedWidget', 'onDone']);
const widgets = defineModel<WidgetObjectSorted[]>('widgets');

const { widgetsByCategory } = useWidgets();
const analyticsStore = useAnalyticsStore();
const userStore = useUserStore();

const loading = ref(false);
const visible = ref(false);
const previewWidget = ref<WidgetPreview | null>(null);

const chosenCategory = ref(widgetsByCategory.value[0].id);
const widgetIds = computed(() => widgets.value?.map((widget) => widget.id));
const widgetsToChange = ref<number[]>([]);

// Because the analyticsStore.chosenEvent might not be available immediately,
// we need to watch for changes and set the chosenCategory to the first category
// in the widgetsByCategory array
watch(
  widgetsByCategory,
  (newWidgetsByCategory) => {
    chosenCategory.value = newWidgetsByCategory[0].id;
  },
  { immediate: true },
);

const handleWidgetClicked = (categoryEnabled: boolean | undefined, widgetId: number) => {
  if (!categoryEnabled) {
    return;
  }
  if (widgetsToChange.value.includes(widgetId)) {
    widgetsToChange.value = widgetsToChange.value.filter((wid) => wid !== widgetId);
  } else {
    widgetsToChange.value.push(widgetId);
  }
};

const widgetInUse = (widgetId: number) => {
  const isSaved = widgetIds.value?.includes(widgetId);
  const wasChanged = widgetsToChange.value.includes(widgetId);

  if (isSaved && wasChanged) {
    return false;
  } else if (isSaved || wasChanged) {
    return true;
  }

  return false;
};

const onLearnMore = (widget: WidgetPreview) => {
  previewWidget.value = widget;
}

const resetPreview = () => {
  previewWidget.value = null;
}


const closeModal = () => {
  widgetsToChange.value = [];
  visible.value = false;
  emit('onDone');
};

const saveChanges = async () => {
  const eventId = analyticsStore.chosenEvent?.id;

  if (!eventId) {
    return;
  }

  const widgetsToRemove = widgetsToChange.value.filter((widget) =>
    widgetIds.value?.includes(widget),
  );
  const widgetsToAdd = widgetsToChange.value.filter((widget) => !widgetIds.value?.includes(widget));

  // Removals First
  if (widgetsToRemove.length) {
    for (const widId of widgetsToRemove) {
      // Update the Grid
      emit('onRemovedWidget', widId);
    }
  }

  // Additions
  if (widgetsToAdd.length) {
    for (const widId of widgetsToAdd) {
      const allWidgets = widgetsByCategory.value.flatMap((cat) => cat.widgets);
      const widget = allWidgets.find((widget) => widget.id === widId);

      if (!widget) {
        return;
      }

      // Get the Widget
      const response = await getWidgetById(userStore.accessToken, eventId, widget.id);
      const widgetData = response;

      widgetData.position = {
        id: widget.id,
        i: analyticsStore.chosenWidgets.length + 1,
        w: widget.default_width ?? 1,
        h: widget.default_height ?? 2,
        x: 0,
        y: 9999,
      };

      const newWidget = {
        id: widget.id,
        i: analyticsStore.chosenWidgets.length + 1,
        w: widget.default_width ?? 1,
        h: widget.default_height ?? 2,
        x: 0,
        y: 9999,
        widget: response,
      };

      analyticsStore.chosenWidgets.push(newWidget);
      await nextTick();
      emit('onAddedWidget', widget.id);
    }
  }
  closeModal();
};
</script>

<style lang="scss">
@use '@/assets/components/modal-widgets.scss';
</style>
